import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Settings, Users, Layers, X, UserCog } from "lucide-react";
import {
  AdminPageConfig,
  RoleOption,
  fetchAdminConfig,
  fetchRoles,
  saveAdminPageConfig,
} from "./AccessControlService";
import { findManifestPage } from "./manifest";
import { useAccessControl } from "./AccessControlContext";
import { RolePicker } from "./RolePicker";

interface Props {
  pageKey: string;
  show: boolean;
  onClose: () => void;
}

const AccessControlEditorModal: React.FC<Props> = ({ pageKey, show, onClose }) => {
  const manifest = findManifestPage(pageKey);
  const { refresh } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [pageConfig, setPageConfig] = useState<AdminPageConfig>({
    viewRoleIds: [],
    adminRoleIds: [],
    elements: {},
  });

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([fetchAdminConfig(), fetchRoles()])
      .then(([cfg, rs]) => {
        if (cancelled) return;
        setRoles(rs);
        const existing = cfg.pages?.[pageKey] ?? { viewRoleIds: [], adminRoleIds: [], elements: {} };
        setPageConfig({
          viewRoleIds: existing.viewRoleIds ?? [],
          adminRoleIds: existing.adminRoleIds ?? [],
          elements: { ...(existing.elements ?? {}) },
        });
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, pageKey]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAdminPageConfig(pageKey, pageConfig);
      await refresh();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updateElement = (key: string, ids: number[]) => {
    setPageConfig((prev) => ({
      ...prev,
      elements: { ...(prev.elements ?? {}), [key]: ids },
    }));
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      enforceFocus={false}
    >
      <Modal.Header
        style={{
          background: "var(--gradient-brand)",
          color: "var(--text-on-brand)",
          border: "none",
          padding: "1.25rem 1.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Settings size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <Modal.Title style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
              Page Access Control
            </Modal.Title>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{manifest?.label ?? pageKey}</div>
          </div>
          <X size={20} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: 0, maxHeight: "70vh", overflowY: "auto", background: "var(--surface-page)" }}>
        {loading && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <Spinner animation="border" variant="primary" size="sm" /> Loading…
          </div>
        )}
        {error && !loading && (
          <div style={{ padding: 16, background: "var(--status-danger-bg)", color: "var(--status-danger)", borderBottom: "1px solid var(--status-danger-border)" }}>
            {error}
          </div>
        )}
        {!loading && !manifest && (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>
            This page isn't registered in the manifest yet. Add it to
            <code style={{ margin: "0 4px" }}>iteach/src/access-control/manifest.ts</code>.
          </div>
        )}
        {!loading && manifest && (
          <>
            <section style={{ padding: "20px 24px", background: "var(--surface-card)", borderBottom: "1px solid var(--border-subtle)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Users size={18} color="var(--brand-primary)" />
                <h5 style={{ margin: 0, fontWeight: 600, color: "var(--text-strong)" }}>Who can open this page</h5>
              </div>
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
                Leave empty to allow everyone. Root can always access.
              </p>
              <RolePicker
                roles={roles}
                selected={pageConfig.viewRoleIds ?? []}
                onChange={(ids) => setPageConfig((p) => ({ ...p, viewRoleIds: ids }))}
              />
            </section>

            <section style={{ padding: "20px 24px", background: "var(--surface-card)", borderBottom: "1px solid var(--border-subtle)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <UserCog size={18} color="#db2777" />
                <h5 style={{ margin: 0, fontWeight: 600, color: "var(--text-strong)" }}>Who can edit these settings</h5>
              </div>
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
                Delegated admins for this page. They'll see the Settings button and can edit roles (including granting to other roles). Root is always implicit.
              </p>
              <RolePicker
                roles={roles}
                selected={pageConfig.adminRoleIds ?? []}
                onChange={(ids) => setPageConfig((p) => ({ ...p, adminRoleIds: ids }))}
                placeholder="Only Root (click to delegate)"
                accent="#db2777"
              />
            </section>

            <section style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Layers size={18} color="var(--brand-primary)" />
                <h5 style={{ margin: 0, fontWeight: 600, color: "var(--text-strong)" }}>Buttons & elements</h5>
                <span
                  style={{
                    fontSize: 11,
                    background: "var(--brand-primary-subtle)",
                    color: "var(--brand-primary-active)",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontWeight: 500,
                  }}
                >
                  {manifest.elements.length}
                </span>
              </div>

              {(() => {
                const groups: { label: string; items: typeof manifest.elements }[] = [];
                for (const el of manifest.elements) {
                  const gLabel = el.group ?? "Other";
                  const existing = groups.find((g) => g.label === gLabel);
                  if (existing) existing.items.push(el);
                  else groups.push({ label: gLabel, items: [el] });
                }
                return groups.map((grp) => (
                  <div key={grp.label} style={{ marginBottom: 18 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        color: "var(--text-muted)",
                        margin: "4px 2px 8px",
                      }}
                    >
                      {grp.label}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {grp.items.map((el) => (
                        <div
                          key={el.key}
                          style={{
                            padding: 14,
                            background: "var(--surface-card)",
                            border: "1px solid var(--border-default)",
                            borderRadius: 10,
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr)",
                            gap: 16,
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-strong)" }}>{el.label}</div>
                          </div>
                          <RolePicker
                            roles={roles}
                            selected={pageConfig.elements?.[el.key] ?? []}
                            onChange={(ids) => updateElement(el.key, ids)}
                            placeholder="Visible to all"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </section>
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "var(--surface-page)", borderTop: "1px solid var(--border-default)" }}>
        <Button variant="secondary" onClick={onClose} disabled={saving} style={{ margin: 2 }}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading} style={{ margin: 2 }}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" /> Saving…
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccessControlEditorModal;
